import { Stack } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs, PageTitle, Tabs } from 'core/components';
import { LabeledValue } from 'core/elements';
import { SelectionCountProvider } from 'core/states';
import { IBreadcrumbsItem, ITab } from 'core/types';
import { ProjectType } from 'projects/constants';
import { projectListPath } from 'projects/routes';
import { useProjectJobsContext } from 'projects/states';
import { IProjectDetails } from 'projects/types';
import { CustomerQuoteList, VendorQuoteList } from 'quotes/components';
import { QuoteEntityType, QuoteListType } from 'quotes/enums';
import { Direction } from 'theme/enums';

import { ProjectDetailsDrawerButton } from './ProjectDetailsDrawer';
import { ExternalCustomerList, JobList, PricingProfileList, UserList } from './tabs';

enum TabValue {
  COST_PRICE = 'costPrice',
  CUSTOMER_ORDERS = 'customerOrders',
  CUSTOMER_QUOTES = 'customerQuotes',
  EXTERNAL_CUSTOMERS = 'externalCustomers',
  JOBS = 'jobs',
  ORDERS = 'orders',
  PRICING = 'pricing',
  QUOTES = 'quotes',
  USERS = 'users',
  VENDOR_ORDERS = 'vendorOrders',
  VENDOR_QUOTES = 'vendorQuotes',
}

interface IProps {
  project: IProjectDetails;
}

export const ProjectDetails = ({ project }: IProps) => {
  const { t } = useTranslation();

  const { jobsCount } = useProjectJobsContext();

  const breadcrumbs: IBreadcrumbsItem[] = [
    {
      text: t('labelProjects'),
      to: projectListPath(),
    },
    {
      text: project.name,
    },
  ];

  const buttons = useMemo(() => {
    return [<ProjectDetailsDrawerButton key="projectDetailsDrawerButton" project={project} />];
  }, [project]);

  const tabs: ITab[] = useMemo(() => {
    return [
      {
        label: t('labelProjectJobsCount', { jobsCount }),
        title: t('labelProjectJobs'),
        value: TabValue.JOBS,
        content: <JobList projectId={project.id} />,
      },
      {
        label: t('labelQuotes'),
        value: TabValue.QUOTES,
        subTabs: [
          {
            label: t('labelCustomerQuotes'),
            title: t('labelCustomerQuotes'),
            value: TabValue.CUSTOMER_QUOTES,
            content: (
              <CustomerQuoteList quoteListType={QuoteListType.PROJECT} projectId={project.id} />
            ),
          },
          {
            label: t('labelVendorQuotes'),
            title: t('labelVendorQuotes'),
            value: TabValue.VENDOR_QUOTES,
            content: (
              <VendorQuoteList
                isDtdProject={project.type === ProjectType.DAY_TO_DAY}
                quoteListType={QuoteListType.PROJECT}
                projectId={project.id}
              />
            ),
          },
        ],
      },
      {
        label: t('labelOrders'),
        value: TabValue.ORDERS,
        subTabs: [
          {
            label: t('labelCustomerOrders'),
            title: t('labelCustomerOrders'),
            value: TabValue.CUSTOMER_ORDERS,
            content: (
              <CustomerQuoteList
                entityType={QuoteEntityType.ORDER}
                quoteListType={QuoteListType.PROJECT}
                projectId={project.id}
              />
            ),
          },
          {
            label: t('labelVendorOrders'),
            title: t('labelVendorOrders'),
            value: TabValue.VENDOR_ORDERS,
            content: (
              <VendorQuoteList
                entityType={QuoteEntityType.ORDER}
                isDtdProject={project.type === ProjectType.DAY_TO_DAY}
                quoteListType={QuoteListType.PROJECT}
                projectId={project.id}
              />
            ),
          },
        ],
      },
      {
        label: t('labelExternalCustomers'),
        value: TabValue.EXTERNAL_CUSTOMERS,
        content: <ExternalCustomerList project={project} />,
        hide: !project.externalProject?.id,
      },
      {
        label: t('labelCostPrice'),
        value: TabValue.COST_PRICE,
        hide: project.type === ProjectType.DAY_TO_DAY,
        subTabs: [
          {
            label: t('labelPricing'),
            title: t('labelPricing'),
            value: TabValue.PRICING,
            content: <PricingProfileList projectId={project.id} />,
          },
        ],
      },
      {
        label: t('labelUsers'),
        value: TabValue.USERS,
        content: <UserList projectId={project.id} />,
        hide: project.type === ProjectType.DAY_TO_DAY,
      },
    ];
  }, [t, jobsCount, project]);

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageTitle
        title={project.name}
        subtitle={
          project.externalProject?.projectNumber || project.externalProject?.programNumber ? (
            <Stack direction={Direction.ROW} spacing={2}>
              {project.externalProject?.projectNumber && (
                <LabeledValue
                  label={t('labelExternalProjectNumber')}
                  value={project.externalProject?.projectNumber}
                  options={{ inline: true }}
                />
              )}
              {project.externalProject?.programNumber && (
                <LabeledValue
                  label={t('labelExternalProgramNumber')}
                  value={project.externalProject?.programNumber}
                  options={{ inline: true }}
                />
              )}
            </Stack>
          ) : null
        }
        buttons={buttons}
      />

      <SelectionCountProvider>
        <Tabs tabs={tabs} ariaLabel="projectDetailsTabs" />
      </SelectionCountProvider>
    </>
  );
};
